// src/pages/analysis-result/index.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";

import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Icon from "../../components/AppIcon";
import { buildPdfReport } from "../../utils/pdfReport";

/* ─── helpers ─────────────────────────────────────────────── */
const isValidNumber = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  const n = Number(v);
  return Number.isFinite(n);
};

const surface = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
};

/* ─── DensityBar ──────────────────────────────────────────── */
function DensityBar({ label, value, color }) {
  if (value === null) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
          {label}
        </span>
        <span
          className="text-xs font-semibold"
          style={{ color, fontFamily: "'JetBrains Mono', monospace" }}
        >
          {value}%
        </span>
      </div>
      <div
        className="w-full h-1.5 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.07)" }}
      >
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${value}%`, background: color }}
        />
      </div>
    </div>
  );
}

/* ─── ImageCard ───────────────────────────────────────────── */
function ImageCard({ title, icon, url, alt, fallback, filename }) {
  const [downloading, setDownloading] = React.useState(false);

  const handleDownload = async () => {
    if (!url) return;
    setDownloading(true);
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || `${alt.replace(/\s+/g, "_")}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      /* CORS fallback: open in new tab */
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden shadow-xl" style={surface}>
      {/* Card header */}
      <div
        className="flex items-center gap-3 px-5 py-4"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
      >
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            background: "rgba(59,130,246,0.15)",
            border: "1px solid rgba(59,130,246,0.25)",
          }}
        >
          <Icon name={icon} size={14} color="#60A5FA" />
        </div>
        <h3 className="text-sm font-semibold tracking-wide" style={{ color: "#CBD5E1" }}>
          {title}
        </h3>

        {url && (
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 disabled:opacity-50"
            style={{
              background: "rgba(59,130,246,0.12)",
              border: "1px solid rgba(59,130,246,0.25)",
              color: "#60A5FA",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(59,130,246,0.22)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(59,130,246,0.12)"; }}
          >
            <Icon name={downloading ? "Loader2" : "Download"} size={12} color="currentColor" className={downloading ? "animate-spin" : ""} />
            {downloading ? "..." : "Download"}
          </button>
        )}
      </div>

      {/* Image */}
      <div className="p-4 flex justify-center">
        {url ? (
          <img
            src={url}
            alt={alt}
            crossOrigin="anonymous"
            className="rounded-xl object-contain"
            style={{
              maxHeight: "380px",
              maxWidth: "100%",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
            onError={() => console.error(`${alt} load error`)}
          />
        ) : (
          <div
            className="w-full h-48 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)" }}
          >
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.2)" }}>
              {fallback}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────── */
export default function AnalysisResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const initial = location.state || null;

  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [manualGraftCount, setManualGraftCount] = useState("");

  const payload = useMemo(() => {
    if (!initial) return null;
    return initial.result || initial.data || initial;
  }, [initial]);

  const patientName = initial?.patientName || payload?.patientName || "";
  const notes = initial?.notes || payload?.notes || "";
  const analysisDate = initial?.analysisDate || payload?.analysisDate || "";

  const graftCount =
    payload?.graft_count ??
    payload?.graftCount ??
    payload?.total_grafts ??
    payload?.count ??
    null;

  const graftCountDisplay = useMemo(() => {
    if (payload?.estimatedGraftCountLabel)
      return payload.estimatedGraftCountLabel.replace(" grafts", "");
    if (!isValidNumber(graftCount)) return null;
    const base = Number(graftCount);
    if (base <= 0) return null;
    const minV = Math.floor(base * 1.1);
    const maxV = Math.ceil(base * 1.25);
    return `${minV}–${maxV}`;
  }, [graftCount, payload]);

  const highDensityPercent =
    payload?.highDensityPercent ?? payload?.densityHigh ?? null;
  const lowDensityPercent =
    payload?.lowDensityPercent ?? payload?.densityLow ?? null;

  const pickUrl = (...keys) => {
    for (const k of keys) {
      const v = payload?.[k] ?? initial?.[k];
      if (typeof v === "string" && v.trim().length > 5) return v.trim();
    }
    return "";
  };

  const originalUrl = pickUrl("original_url", "originalUrl", "input_url", "inputUrl");
  const overlayUrl = pickUrl(
    "overlay_url", "overlayUrl", "marked_image_url",
    "markedImageUrl", "processed_image_url", "processedImageUrl"
  );
  const heatmapUrl = pickUrl("heatmap_url", "heatmapUrl", "density_map_url", "densityMapUrl");

  const hasData = !!payload;

  const breadcrumbItems = [
    { label: "Dashboard", path: "/dashboard" },
    { label: "Analysis Result", path: "/analysis-result" },
  ];

  const handleDownloadReport = async () => {
    if (!hasData) return;
    if (!overlayUrl && !heatmapUrl) {
      alert("Report images are not available yet.");
      return;
    }

    setIsPdfLoading(true);
    try {
      const safeName = (patientName || "Patient")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^\w-]/g, "");

      await buildPdfReport({
        patientName:       patientName || "Unknown Patient",
        analysisDate,
        detectedCount:     isValidNumber(graftCount)
                             ? Math.round(Number(graftCount)).toLocaleString()
                             : "—",
        estimatedRange:    graftCountDisplay ?? "—",
        surgeonCount:      manualGraftCount.trim() || null,
        highDensityPercent,
        lowDensityPercent,
        notes,
        overlayUrl,
        heatmapUrl,
        filename: `${safeName}_Report.pdf`,
      });
    } catch (err) {
      console.error("PDF oluşturulamadı:", err);
      alert("PDF oluşturulamadı. Konsolu kontrol edin.");
    } finally {
      setIsPdfLoading(false);
    }
  };

  /* ─── RENDER ─────────────────────────────────────────────── */
  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: "#060d1c" }}
    >
      {/* CSS grid */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(59,130,246,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.05) 1px, transparent 1px)",
          backgroundSize: "52px 52px",
        }}
      />

      {/* Ambient glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute"
          style={{
            top: "-100px",
            right: "-80px",
            width: "600px",
            height: "600px",
            background:
              "radial-gradient(circle, rgba(59,130,246,0.09) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute"
          style={{
            bottom: "-60px",
            left: "-40px",
            width: "500px",
            height: "500px",
            background:
              "radial-gradient(circle, rgba(6,182,212,0.07) 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="relative z-10 min-h-screen overflow-y-auto pb-24">
        <Header />

        <main className="pt-10 px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* ── Page header ── */}
            <div className="mt-8 rounded-2xl p-7 shadow-xl" style={surface}>
              <div className="flex items-center gap-3 mb-1">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: "linear-gradient(135deg, rgba(59,130,246,0.22), rgba(6,182,212,0.14))",
                    border: "1px solid rgba(59,130,246,0.3)",
                  }}
                >
                  <Icon name="ScanSearch" size={17} color="#60A5FA" />
                </div>
                <h1
                  className="text-2xl font-light tracking-tight"
                  style={{ color: "#E2E8F0" }}
                >
                  Image Analysis Result
                </h1>

                {hasData && (
                  <span
                    className="ml-auto flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full"
                    style={{
                      background: "rgba(16,185,129,0.12)",
                      border: "1px solid rgba(16,185,129,0.25)",
                      color: "#34D399",
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Analysis Complete
                  </span>
                )}
              </div>
              <p className="ml-12 text-sm" style={{ color: "#64748B" }}>
                Review graft estimation outputs and generate a clinical PDF report.
              </p>

              {!hasData && (
                <div
                  className="mt-6 rounded-xl p-5"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <p className="text-sm mb-4" style={{ color: "#94A3B8" }}>
                    Result data not found. This can happen if you refreshed the page.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => navigate("/new-analysis")}
                      className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150"
                      style={{
                        background: "linear-gradient(135deg, #1D4ED8, #0EA5E9)",
                        color: "#fff",
                        boxShadow: "0 4px 16px rgba(29,78,216,0.3)",
                      }}
                    >
                      Start New Analysis
                    </button>
                    <Link
                      to="/dashboard"
                      className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150"
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      Back to Dashboard
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {hasData && (
              <>
                {/* ── Metrics + Actions Grid ── */}
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-5">

                  {/* Left: Patient + Metrics */}
                  <div className="lg:col-span-8 space-y-5">

                    {/* Patient Info */}
                    <div className="rounded-2xl p-6 shadow-xl" style={surface}>
                      <div className="flex items-center gap-2 mb-4">
                        <Icon name="User" size={14} color="#64748B" />
                        <span
                          className="text-xs font-semibold tracking-widest uppercase"
                          style={{ color: "rgba(255,255,255,0.3)" }}
                        >
                          Patient Record
                        </span>
                      </div>

                      <div className="flex items-baseline gap-3 flex-wrap">
                        <h2
                          className="text-xl font-medium"
                          style={{ color: "#E2E8F0" }}
                        >
                          {patientName || "—"}
                        </h2>
                        {analysisDate && (
                          <span
                            className="text-sm"
                            style={{
                              color: "rgba(255,255,255,0.3)",
                              fontFamily: "'JetBrains Mono', monospace",
                            }}
                          >
                            {analysisDate}
                          </span>
                        )}
                      </div>

                      {notes && (
                        <p
                          className="text-sm mt-3 leading-relaxed whitespace-pre-wrap"
                          style={{ color: "#64748B" }}
                        >
                          {notes}
                        </p>
                      )}
                    </div>

                    {/* Graft count metrics */}
                    <div
                      className="rounded-2xl shadow-xl overflow-hidden"
                      style={surface}
                    >
                      {/* Top accent */}
                      <div
                        className="h-[1px] w-full"
                        style={{
                          background:
                            "linear-gradient(90deg, transparent, #3B82F6 40%, #06B6D4 60%, transparent)",
                        }}
                      />

                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-6">
                          <Icon name="BarChart3" size={14} color="#64748B" />
                          <span
                            className="text-xs font-semibold tracking-widest uppercase"
                            style={{ color: "rgba(255,255,255,0.3)" }}
                          >
                            Graft Analysis
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                          {/* Detected count */}
                          <div>
                            <p
                              className="text-xs mb-2"
                              style={{ color: "rgba(255,255,255,0.3)" }}
                            >
                              Detected Graft Count
                            </p>
                            <p
                              className="text-5xl font-light leading-none"
                              style={{
                                color: "#22D3EE",
                                fontFamily: "'JetBrains Mono', monospace",
                                textShadow: "0 0 20px rgba(34,211,238,0.3)",
                              }}
                            >
                              {isValidNumber(graftCount)
                                ? Math.round(Number(graftCount)).toLocaleString()
                                : "—"}
                            </p>
                            <p
                              className="text-xs mt-1.5"
                              style={{ color: "rgba(255,255,255,0.2)" }}
                            >
                              follicular units detected
                            </p>
                          </div>

                          {/* Estimated range */}
                          <div>
                            <p
                              className="text-xs mb-2"
                              style={{ color: "rgba(255,255,255,0.3)" }}
                            >
                              Estimated Range
                            </p>
                            <p
                              className="text-5xl font-light leading-none"
                              style={{
                                color: "#60A5FA",
                                fontFamily: "'JetBrains Mono', monospace",
                                textShadow: "0 0 20px rgba(96,165,250,0.25)",
                              }}
                            >
                              {graftCountDisplay ?? "—"}
                            </p>
                            <p
                              className="text-xs mt-1.5"
                              style={{ color: "rgba(255,255,255,0.2)" }}
                            >
                              grafts (±10–25% clinical estimate)
                            </p>
                          </div>

                          {/* Manual override */}
                          <div>
                            <p
                              className="text-xs mb-2"
                              style={{ color: "rgba(255,255,255,0.3)" }}
                            >
                              Surgeon's Final Count
                            </p>
                            <input
                              type="number"
                              min="0"
                              placeholder="Enter count"
                              value={manualGraftCount}
                              onChange={(e) => setManualGraftCount(e.target.value)}
                              className="w-full rounded-xl px-4 py-3 text-2xl font-light outline-none transition-all duration-150"
                              style={{
                                background: "rgba(255,255,255,0.04)",
                                border: manualGraftCount
                                  ? "1px solid rgba(251,191,36,0.5)"
                                  : "1px solid rgba(255,255,255,0.1)",
                                color: manualGraftCount ? "#FBBf24" : "rgba(255,255,255,0.25)",
                                fontFamily: "'JetBrains Mono', monospace",
                                caretColor: "#FBBf24",
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.border = "1px solid rgba(251,191,36,0.6)";
                                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(251,191,36,0.08)";
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.border = manualGraftCount
                                  ? "1px solid rgba(251,191,36,0.5)"
                                  : "1px solid rgba(255,255,255,0.1)";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            />
                            <p
                              className="text-xs mt-1.5"
                              style={{ color: manualGraftCount ? "rgba(251,191,36,0.6)" : "rgba(255,255,255,0.2)" }}
                            >
                              {manualGraftCount ? "overrides report count" : "used in PDF if filled"}
                            </p>
                          </div>
                        </div>

                        {/* Density bars */}
                        {(highDensityPercent !== null || lowDensityPercent !== null) && (
                          <div
                            className="mt-6 pt-5 space-y-3"
                            style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
                          >
                            <p
                              className="text-xs font-semibold tracking-widest uppercase mb-3"
                              style={{ color: "rgba(255,255,255,0.25)" }}
                            >
                              Density Distribution
                            </p>
                            <DensityBar
                              label="High Density"
                              value={highDensityPercent}
                              color="#22D3EE"
                            />
                            <DensityBar
                              label="Low Density"
                              value={lowDensityPercent}
                              color="#60A5FA"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="lg:col-span-4">
                    <div
                      className="rounded-2xl p-5 shadow-xl sticky top-5 space-y-3"
                      style={surface}
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <Icon name="FileDown" size={14} color="#64748B" />
                        <span
                          className="text-xs font-semibold tracking-widest uppercase"
                          style={{ color: "rgba(255,255,255,0.3)" }}
                        >
                          Actions
                        </span>
                      </div>

                      {/* PDF Download */}
                      <button
                        onClick={handleDownloadReport}
                        disabled={isPdfLoading}
                        className="w-full py-3.5 rounded-xl text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={
                          isPdfLoading
                            ? {
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                color: "rgba(255,255,255,0.3)",
                              }
                            : {
                                background: "linear-gradient(135deg, #1D4ED8, #0EA5E9)",
                                color: "#fff",
                                boxShadow: "0 6px 20px rgba(29,78,216,0.4)",
                              }
                        }
                        onMouseEnter={(e) => {
                          if (!isPdfLoading) {
                            e.currentTarget.style.boxShadow = "0 8px 28px rgba(29,78,216,0.6)";
                            e.currentTarget.style.transform = "translateY(-1px)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = isPdfLoading ? "none" : "0 6px 20px rgba(29,78,216,0.4)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        <Icon
                          name={isPdfLoading ? "Loader2" : "Download"}
                          size={15}
                          color="currentColor"
                          className={isPdfLoading ? "animate-spin" : ""}
                        />
                        {isPdfLoading ? "Generating PDF..." : "Download PDF Report"}
                      </button>

                      {/* Divider */}
                      <div
                        className="my-1"
                        style={{
                          height: "1px",
                          background: "rgba(255,255,255,0.06)",
                        }}
                      />

                      {/* New Analysis */}
                      <button
                        onClick={() => navigate("/new-analysis")}
                        className="w-full py-3 rounded-xl text-sm font-medium transition-all duration-150 flex items-center justify-center gap-2"
                        style={{
                          background: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          color: "rgba(255,255,255,0.7)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,0.09)";
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                          e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                        }}
                      >
                        <Icon name="Plus" size={14} color="currentColor" />
                        New Analysis
                      </button>

                      {/* Dashboard */}
                      <button
                        onClick={() => navigate("/dashboard")}
                        className="w-full py-3 rounded-xl text-sm transition-all duration-150 flex items-center justify-center gap-2"
                        style={{
                          background: "transparent",
                          color: "rgba(255,255,255,0.3)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "rgba(255,255,255,0.3)";
                        }}
                      >
                        <Icon name="LayoutDashboard" size={13} color="currentColor" />
                        Back to Dashboard
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Images ── */}
                <div className="mt-5 space-y-5">
                  <ImageCard
                    title="Detected Grafts Overlay"
                    icon="ScanSearch"
                    url={overlayUrl}
                    alt="Overlay"
                    fallback="Overlay image not available"
                    filename={`${(patientName || "patient").replace(/\s+/g, "_")}_overlay.jpg`}
                  />

                  <ImageCard
                    title="Density Heatmap"
                    icon="Map"
                    url={heatmapUrl}
                    alt="Heatmap"
                    fallback="Heatmap not available"
                    filename={`${(patientName || "patient").replace(/\s+/g, "_")}_heatmap.jpg`}
                  />

                  {originalUrl && (
                    <ImageCard
                      title="Original Image"
                      icon="Camera"
                      url={originalUrl}
                      alt="Original"
                      fallback="Original image not available"
                      filename={`${(patientName || "patient").replace(/\s+/g, "_")}_original.jpg`}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
